import { PhoneCallbackOutlined, PhoneDisabledOutlined } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import type { TranslationKey } from '@/i18n/translation-key';
import type { FlowContributorStatusIconColor } from '@/utils/contributor-extraction.utils';
import { contributorRemoteSpeakerStatusIconTooltip } from '@/components/events/detail/tabs/common/contributors/contributor-item-action-labels';

const statusIconButtonSx = {
  '& > svg': {
    fontSize: '1.5rem',
    width: '1.5rem',
    height: '1.5rem',
  },
} as const;

/** Keep layout identical to clickable IconButtons when the status icon is display-only. */
const nonInteractiveIconButtonSx = {
  ...statusIconButtonSx,
  'cursor': 'default',
  '&:hover': { backgroundColor: 'transparent' },
} as const;

function toMuiIconColor(color: FlowContributorStatusIconColor): 'primary' | 'info' | 'warning' | 'success' {
  if (color === 'primary') return 'primary';
  if (color === 'warning') return 'warning';
  if (color === 'success') return 'success';
  return 'info';
}

export type ContributorSlotRemoteSpeakerIconProps = {
  t: (key: TranslationKey) => string;
  showIcon: boolean;
  slotIsRemote: boolean;
  iconColor: FlowContributorStatusIconColor | null;
  onEnableRemoteSpeaker?: () => void;
  onPendingDayClick?: () => void;
  dayStatus: 'inactive' | 'pending' | 'active';
  loading?: boolean;
  disabled?: boolean;
};

export function ContributorSlotRemoteSpeakerIcon({
  t,
  showIcon,
  slotIsRemote,
  iconColor,
  onEnableRemoteSpeaker,
  onPendingDayClick,
  dayStatus,
  loading = false,
  disabled = false,
}: ContributorSlotRemoteSpeakerIconProps) {
  if (!showIcon) return null;

  const actionSpinner = <CircularProgress color="info" size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />;

  if (!slotIsRemote) {
    const tooltip = contributorRemoteSpeakerStatusIconTooltip(t, { slotIsRemote: false });
    return (
      <Tooltip title={tooltip} placement="left" disableInteractive>
        <IconButton
          size="small"
          disabled={disabled || loading || !onEnableRemoteSpeaker}
          aria-label={tooltip}
          data-testid="contributor-slot-remote-speaker-enable"
          onClick={(event) => {
            event.stopPropagation();
            onEnableRemoteSpeaker?.();
          }}
          sx={statusIconButtonSx}
        >
          {loading ? actionSpinner : <PhoneDisabledOutlined fontSize="small" color="action" />}
        </IconButton>
      </Tooltip>
    );
  }

  const pendingClickable = dayStatus === 'pending' && Boolean(onPendingDayClick);
  const tooltip = contributorRemoteSpeakerStatusIconTooltip(t, {
    slotIsRemote: true,
    color: iconColor ?? (dayStatus === 'pending' ? 'primary' : 'info'),
  });
  const icon = (
    <PhoneCallbackOutlined
      fontSize="small"
      color={toMuiIconColor(iconColor ?? 'info')}
      sx={{ transform: 'scaleX(-1)' }}
    />
  );

  return (
    <Tooltip title={tooltip} placement="left" disableInteractive>
      <IconButton
        size="small"
        disabled={disabled || loading}
        disableRipple={!pendingClickable}
        tabIndex={pendingClickable ? undefined : -1}
        aria-label={tooltip}
        data-testid="contributor-slot-remote-speaker-status"
        onClick={(event) => {
          event.stopPropagation();
          if (pendingClickable) {
            onPendingDayClick?.();
          }
        }}
        sx={pendingClickable ? statusIconButtonSx : nonInteractiveIconButtonSx}
      >
        {loading ? actionSpinner : icon}
      </IconButton>
    </Tooltip>
  );
}
